import React from 'react';
import { CheckCircle, AlertTriangle, Upload, Eye } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import { generateMappingPreview, validateMappings } from '../../../utils/fieldMapper';

const ImportPreviewModal = ({ 
  isOpen, 
  onClose, 
  parsedData, 
  mappings, 
  customFields, 
  onConfirmImport 
}) => {
  if (!parsedData || !mappings) return null;

  const validation = validateMappings(mappings, customFields);
  const preview = generateMappingPreview(mappings, parsedData.rows);
  
  // Get mapped fields for display
  const mappedFields = Object.values(mappings).filter(field => field !== null);
  
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <h2 className="text-xl font-semibold text-gray-900">Import Preview</h2>
        <p className="text-sm text-gray-600 mt-1">
          Review how your data will be imported before proceeding
        </p>
      </Modal.Header>

      <Modal.Content>
        {/* Summary Statistics */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{parsedData.totalRows}</div>
            <div className="text-sm text-blue-800">Total Rows</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{mappedFields.length}</div>
            <div className="text-sm text-green-800">Mapped Fields</div>
          </div>
          <div className={`border rounded-lg p-4 text-center ${
            validation.errors.length === 0 
              ? 'bg-green-50 border-green-200' 
              : 'bg-red-50 border-red-200'
          }`}>
            <div className={`text-2xl font-bold ${
              validation.errors.length === 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {validation.errors.length}
            </div>
            <div className={`text-sm ${
              validation.errors.length === 0 ? 'text-green-800' : 'text-red-800'
            }`}>
              Errors
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{validation.warnings.length}</div>
            <div className="text-sm text-yellow-800">Warnings</div>
          </div>
        </div>

        {/* Validation Messages */}
        {(validation.errors.length > 0 || validation.warnings.length > 0) && (
          <div className="mb-6 space-y-2">
            {validation.errors.map((error, index) => (
              <div key={`error-${index}`} className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-700">{error.message}</p>
              </div>
            ))}
            {validation.warnings.map((warning, index) => (
              <div key={`warning-${index}`} className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
                <p className="text-sm text-yellow-700">{warning.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* Field Mapping Summary */}
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-900 mb-3">Field Mappings</h3>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(mappings)
                .filter(([, field]) => field !== null)
                .map(([csvColumn, customField]) => (
                  <div key={csvColumn} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{csvColumn}</span>
                    <span className="text-sm font-medium text-gray-900">
                      {customField.field_label}
                      {customField.is_required && (
                        <span className="ml-1 text-xs text-red-600">(Required)</span>
                      )}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Data Preview */}
        <div>
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Data Preview (First 5 rows)
          </h3>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Row
                    </th>
                    {mappedFields.map(field => (
                      <th key={field.field_name} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        {field.field_label}
                        {field.is_required && (
                          <span className="ml-1 text-red-500">*</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {preview.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500 font-mono">
                        {row.rowNumber}
                      </td>
                      {mappedFields.map(field => {
                        const csvColumn = Object.keys(mappings).find(
                          col => mappings[col]?.field_name === field.field_name
                        );
                        const mapping = row.mappings[csvColumn];
                        const value = mapping?.originalValue || '';
                        const isEmpty = !value || value.trim() === '';
                        const isRequired = field.is_required;
                        
                        return (
                          <td 
                            key={field.field_name} 
                            className={`px-3 py-2 ${
                              isEmpty && isRequired 
                                ? 'bg-red-50 text-red-700' 
                                : 'text-gray-900'
                            }`}
                          >
                            <div className="max-w-32 truncate" title={value}>
                              {value || (
                                <span className="text-gray-400 italic">empty</span>
                              )}
                            </div>
                            {isEmpty && isRequired && (
                              <div className="text-xs text-red-600 mt-1">Required field is empty</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {parsedData.totalRows > 5 && (
            <p className="text-sm text-gray-500 mt-2 text-center">
              ... and {parsedData.totalRows - 5} more rows
            </p>
          )}
        </div>

        {/* Import Ready Status */}
        {validation.isValid && (
          <div className="mt-6 flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-900">Ready to Import</p>
              <p className="text-sm text-green-700">
                All required fields are mapped and data looks good. You can proceed with the import.
              </p>
            </div>
          </div>
        )}
      </Modal.Content>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} className="gap-2">
          <Eye className="h-4 w-4" />
          Back to Mapping
        </Button>
        <Button
          onClick={onConfirmImport}
          disabled={!validation.isValid}
          className="gap-2"
        >
          <Upload className="h-4 w-4" />
          Confirm Import
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImportPreviewModal;
